import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { COLORS_ACTOR, COLOR_ACTOR_PER_DEFECTE } from '../utils/actorColors';
import { colorDePersona } from '../utils/presenciaColors';
import { toast } from 'react-toastify';
import { strategyMappingService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ELEMENT_TYPES = {
  ACTOR: 'actor',
  CAPABILITY: 'capability',
  OPPORTUNITY: 'opportunity',
  INTENTION: 'intention'
};

const ELEMENT_COLORS = {
  [ELEMENT_TYPES.ACTOR]: '#475569', // Gris pissarra — el de per defecte
  [ELEMENT_TYPES.CAPABILITY]: null, // Fa servir el color del seu actor
  // Les oportunitats no pertanyen a cap actor i han de tenir color propi. Abans
  // era el mateix gris que el de per defecte dels actors, o sigui que una
  // oportunitat i un actor sense color triat es veien exactament igual.
  [ELEMENT_TYPES.OPPORTUNITY]: '#334155',
  [ELEMENT_TYPES.INTENTION]: null // Fa servir el color del seu actor
};


// Geometria de les caixes, en un sol lloc.
//
// L'amplada és fixa perquè les posicions es desen per element i una amplada
// variable faria que les caixes se solapessin al canviar-ne el nom. L'alçada sí
// que creix segons el text. Les fletxes s'ancoraven a uns 160/65/32,5 escrits a
// mà que ja no quadraven —les caixes sense actor fan 50 d'alt i la fletxa
// sortia a 65—, i ara surten d'aquí mateix.
const AMPLE_CAIXA = 160;
const MARGE_TEXT = 12;          // marge horitzontal per al text de dins
const MAX_LINIES_TITOL = 2;     // més enllà, s'escurça amb punts suspensius

// Talla un títol en línies que càpiguen a la caixa. SVG no ajusta el text ni el
// retalla: un <text> massa llarg surt per tots dos costats de la caixa, que és
// exactament el que passava. L'amplada d'un caràcter s'estima en 0,55 em, que
// per a una font de sistema en negreta és una aproximació prou bona.
const partirTitol = (text, midaLletra = 13) => {
  const ampleUtil = AMPLE_CAIXA - MARGE_TEXT * 2;
  const perLinia = Math.max(6, Math.floor(ampleUtil / (midaLletra * 0.55)));
  const paraules = String(text || '').split(/\s+/).filter(Boolean);
  const linies = [];
  let actual = '';

  for (const p of paraules) {
    const prova = actual ? `${actual} ${p}` : p;
    if (prova.length <= perLinia) {
      actual = prova;
    } else {
      if (actual) linies.push(actual);
      // Una paraula sola més llarga que la línia es talla igualment
      actual = p.length > perLinia ? `${p.slice(0, perLinia - 1)}…` : p;
    }
    if (linies.length === MAX_LINIES_TITOL) break;
  }
  if (actual && linies.length < MAX_LINIES_TITOL) linies.push(actual);

  // Si encara sobren paraules, s'indica amb punts suspensius a l'última línia
  const cabut = linies.join(' ').replace(/…$/, '');
  if (cabut.replace(/\s+/g, ' ').length < paraules.join(' ').length) {
    const u = linies.length - 1;
    linies[u] = linies[u].replace(/.{1}$/, '…');
  }
  return linies.length ? linies : [''];
};

// Distribució vertical d'una caixa. La retornen tant el dibuix com el càlcul
// dels ancoratges de les fletxes, perquè no es puguin tornar a desincronitzar.
const distribucioCaixa = (element, ambActor) => {
  const linies = partirTitol(element.name);
  const dalt = 16;                       // marge superior fins a la primera línia
  const altLinia = 15;
  const yTitol = linies.map((_, i) => dalt + i * altLinia);
  const yTipus = dalt + linies.length * altLinia + 2;
  const yActor = ambActor ? yTipus + 13 : null;
  const alt = (ambActor ? yActor : yTipus) + 12;
  return { linies, yTitol, yTipus, yActor, alt: Math.max(50, alt) };
};

// L'alçada d'una caixa depèn del seu contingut, i les fletxes n'han de fer cas.
const alturaCaixa = (element, actors = []) => {
  const actor = element.actor_id ? actors.find(a => a.id === element.actor_id) : null;
  const ambActor = !!actor && element.element_type !== ELEMENT_TYPES.ACTOR;
  return distribucioCaixa(element, ambActor).alt;
};

const TOOLS = {
  SELECT: 'select',
  PAN: 'pan',
  CONNECT: 'connect',
  ADD_ACTOR: 'add_actor',
  ADD_CAPABILITY: 'add_capability',
  ADD_OPPORTUNITY: 'add_opportunity',
  ADD_INTENTION: 'add_intention'
};

const StrategyMapping = ({ case_, onUpdate, collaboration }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  // La pantalla completa s'aplica al contenidor de FORA, que inclou el plafó
  // d'actors i la barra d'eines. Abans s'aplicava només al llenç, i el plafó
  // —que n'és germà, no fill— desapareixia en entrar-hi.
  const fullscreenRef = useRef(null);
  const [actors, setActors] = useState([]);
  const [elements, setElements] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedElements, setSelectedElements] = useState([]);

  // ---------------------------------------------------------------------
  // Qui està fent què, al llenç
  //
  // Això NO es desa enlloc: viatja pel WebSocket i es perd en desconnectar,
  // que és el que ha de fer la presència. Es reaprofita el canal
  // `content_change`, que el servidor reparteix als altres sense tocar la base
  // de dades i excloent-ne qui l'envia.
  //
  // Serveix per a dues coses: veure què té agafat cadascú (un anell del seu
  // color amb el seu nom) i veure les caixes moure's mentre les arrossega, en
  // comptes que et saltin de cop quan deixa anar el ratolí.
  // ---------------------------------------------------------------------
  const [presenciaRemota, setPresenciaRemota] = useState({});   // user_id -> {nom, seleccio, arrossegant}
  const ultimEnviament = useRef(0);

  const anunciaSeleccio = useCallback((ids) => {
    if (!collaboration?.sendContentChange || !collaboration?.isConnected) return;
    collaboration.sendContentChange('canvas_presence', { que: 'seleccio', ids });
  }, [collaboration]);

  const anunciaArrossegament = useCallback((ids, x, y) => {
    if (!collaboration?.sendContentChange || !collaboration?.isConnected) return;
    // Com a molt un avís cada 60 ms: arrossegar dispara desenes d'events per
    // segon i no cal enviar-los tots perquè es vegi fluid.
    const ara = Date.now();
    if (ara - ultimEnviament.current < 60) return;
    ultimEnviament.current = ara;
    collaboration.sendContentChange('canvas_presence', { que: 'arrossega', ids, x, y });
  }, [collaboration]);

  // Cada cop que canvia la teva selecció, els altres se n'assabenten.
  useEffect(() => {
    anunciaSeleccio(selectedElements);
  }, [selectedElements, anunciaSeleccio]);

  // Quan entra algú nou, se li torna a dir què tens seleccionat: si no, no ho
  // sabria fins que ho canviessis.
  useEffect(() => {
    anunciaSeleccio(selectedElements);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboration?.activeUsers?.length]);

  useEffect(() => {
    const rebut = (event) => {
      const { section, content, user } = event.detail || {};
      if (section !== 'canvas_presence' || !user?.user_id) return;
      if (user.user_id === currentUser?.id) return;

      if (content?.que === 'seleccio') {
        setPresenciaRemota(prev => ({
          ...prev,
          [user.user_id]: { ...prev[user.user_id], nom: user.full_name, seleccio: content.ids || [] }
        }));
      } else if (content?.que === 'arrossega') {
        setPresenciaRemota(prev => ({
          ...prev,
          [user.user_id]: { ...prev[user.user_id], nom: user.full_name, seleccio: content.ids || [] }
        }));
        // La posició s'aplica de seguida perquè es vegi el moviment. Quan
        // l'altre deixi anar, el PUT i el seu `entity_change` la faran oficial.
        setElements(prev => prev.map(el =>
          (content.ids || []).includes(el.id)
            ? { ...el, position_x: content.x, position_y: content.y }
            : el
        ));
      }
    };
    window.addEventListener('collaboration-content-change', rebut);
    return () => window.removeEventListener('collaboration-content-change', rebut);
  }, [currentUser?.id]);

  // Qui té agafada cada caixa, per pintar-hi l'anell.
  const propietariRemot = (elementId) => {
    for (const [uid, p] of Object.entries(presenciaRemota)) {
      if ((p?.seleccio || []).includes(elementId)) {
        return { userId: Number(uid), nom: p.nom, color: colorDePersona(uid) };
      }
    }
    return null;
  };
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [viewTransform, setViewTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [connectionStart, setConnectionStart] = useState(null);
  const [showActorModal, setShowActorModal] = useState(false);
  const [editingActor, setEditingActor] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [showElementModal, setShowElementModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [elementForm, setElementForm] = useState({
    id: null, // Add id for editing
    element_type: '',
    name: '',
    description: '',
    actor_id: null,
    position: { x: 0, y: 0 }
  });

  // Helper function to render modals using portals when in fullscreen
  const renderModal = (content) => {
    if (isFullscreen) {
      return createPortal(content, document.body);
    }
    return content;
  };

  // Form states
  const [actorForm, setActorForm] = useState({
    name: '',
    description: '',
    primary_intention: '',
    color: COLOR_ACTOR_PER_DEFECTE
  });

  useEffect(() => {
    if (case_?.id) {
      fetchStrategyMapping();
    }
  }, [case_?.id]);

  // Listen for real-time collaboration changes
  useEffect(() => {
    // Aplica els canvis dels companys directament a l'estat local.
    //
    // Abans això recarregava el mapa sencer cada cop que algú tocava res: si
    // dues persones movien caixes alhora, cadascuna veia saltar la seva. Ara
    // arriba l'objecte concret i s'aplica sol; el que estàs fent tu no es toca.
    const aplicaCanvi = (event) => {
      const { entity, action, data, user } = event.detail || {};
      // L'eco del que has fet tu s'ignora: si no, arrossegar una caixa te la
      // faria saltar a les mans.
      if (!user?.user_id || !currentUser?.id || user.user_id === currentUser.id) return;

      const posa = (setter) => setter(prev => {
        if (action === 'deleted') return prev.filter(x => x.id !== data.id);
        const i = prev.findIndex(x => x.id === data.id);
        if (i === -1) return [...prev, data];
        const copia = [...prev];
        copia[i] = { ...copia[i], ...data };
        return copia;
      });

      if (entity === 'actor') posa(setActors);
      else if (entity === 'element') posa(setElements);
      else if (entity === 'connection') posa(setConnections);
    };

    window.addEventListener('collaboration-entity-change', aplicaCanvi);
    return () => window.removeEventListener('collaboration-entity-change', aplicaCanvi);
  }, [currentUser?.id]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Update canvas size based on container
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Account for toolbox height (~80px) when calculating canvas height
        const toolboxHeight = 80;
        setCanvasSize({
          width: Math.max(1200, rect.width),
          height: Math.max(800, rect.height - toolboxHeight)
        });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [isFullscreen]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await fullscreenRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
      toast.error('Fullscreen not supported on this browser');
    }
  };

  const fetchStrategyMapping = async () => {
    try {
      const data = await strategyMappingService.getStrategyMapping(case_.id);
      setActors(data.actors || []);
      setElements(data.elements || []);
      setConnections(data.connections || []);

    } catch (error) {
      console.error('Error fetching strategy mapping:', error);
      toast.error(t('strategyMapping.fetchError'));
    }
  };

  // Tool handlers
  const handleToolChange = (tool) => {
    // Allow toggling the connect tool off
    if (tool === TOOLS.CONNECT && activeTool === TOOLS.CONNECT) {
      setActiveTool(TOOLS.SELECT);
      setConnectionStart(null);
      setSelectedElements([]);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'default';
      }
      return;
    }
    
    setActiveTool(tool);
    setConnectionStart(null);
    setSelectedElements([]);
    
    if (tool.startsWith('add_')) {
      if (tool === TOOLS.ADD_ACTOR) {
        setShowActorModal(true);
      } else {
        // Set cursor to crosshair for element placement
        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'crosshair';
        }
      }
    } else {
      if (canvasRef.current) {
        canvasRef.current.style.cursor = tool === TOOLS.PAN ? 'grab' : 'default';
      }
    }
  };

  // Canvas interaction handlers
  const getMousePosition = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left - viewTransform.x) / viewTransform.scale,
      y: (event.clientY - rect.top - viewTransform.y) / viewTransform.scale
    };
  };

  const handleCanvasClick = async (event) => {
    const mousePos = getMousePosition(event);
    
    if (activeTool.startsWith('add_') && activeTool !== TOOLS.ADD_ACTOR) {
      // Place new element
      const elementType = activeTool.split('_')[1];
      await createElementAtPosition(elementType, mousePos);
      setActiveTool(TOOLS.SELECT);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'default';
      }
    } else if (activeTool === TOOLS.SELECT) {
      // Clear selection if clicking on empty space
      setSelectedElements([]);
      setConnectionStart(null);
    }
  };

  const handleElementClick = (element, event) => {
    event.stopPropagation();
    
    if (activeTool === TOOLS.CONNECT) {
      if (!connectionStart) {
        setConnectionStart(element);
        setSelectedElements([element.id]);
      } else if (connectionStart.id !== element.id) {
        handleCreateConnection(connectionStart.id, element.id);
        setConnectionStart(null);
        setSelectedElements([]);
        setActiveTool(TOOLS.SELECT);
      }
    } else if (activeTool === TOOLS.SELECT) {
      if (event.ctrlKey || event.metaKey) {
        // Multi-select
        setSelectedElements(prev => 
          prev.includes(element.id) 
            ? prev.filter(id => id !== element.id)
            : [...prev, element.id]
        );
      } else {
        setSelectedElements([element.id]);
      }
    }
  };

  const handleElementMouseDown = (element, event) => {
    if (activeTool === TOOLS.SELECT && !event.ctrlKey && !event.metaKey) {
      setIsDragging(true);
      const mousePos = getMousePosition(event);
      setDragOffset({
        x: mousePos.x - element.position_x,
        y: mousePos.y - element.position_y
      });
    }
  };

  const handleCanvasMouseDown = (event) => {
    if (activeTool === TOOLS.PAN) {
      setIsPanning(true);
      setPanStart({ x: event.clientX, y: event.clientY });
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grabbing';
      }
    }
  };

  const handleCanvasMouseMove = (event) => {
    if (isPanning) {
      const deltaX = event.clientX - panStart.x;
      const deltaY = event.clientY - panStart.y;
      setViewTransform(prev => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setPanStart({ x: event.clientX, y: event.clientY });
    } else if (isDragging && selectedElements.length > 0) {
      const mousePos = getMousePosition(event);
      const targetElement = elements.find(el => selectedElements.includes(el.id));
      if (targetElement) {
        const newX = mousePos.x - dragOffset.x;
        const newY = mousePos.y - dragOffset.y;
        
        setElements(prev => prev.map(el => 
          selectedElements.includes(el.id)
            ? { ...el, position_x: newX, position_y: newY }
            : el
        ));
        anunciaArrossegament(selectedElements, newX, newY);
      }
    }
  };

  const handleCanvasMouseUp = async () => {
    if (isPanning) {
      setIsPanning(false);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grab';
      }
    }
    
    if (isDragging) {
      setIsDragging(false);
      // Save new positions
      for (const elementId of selectedElements) {
        const element = elements.find(el => el.id === elementId);
        if (element) {
          await handleUpdateElement(element.id, {
            position_x: element.position_x,
            position_y: element.position_y
          });
        }
      }
    }
  };

  // Zoom handlers
  const handleZoom = (direction) => {
    const factor = direction === 'in' ? 1.2 : 0.8;
    setViewTransform(prev => ({
      ...prev,
      scale: Math.max(0.1, Math.min(3, prev.scale * factor))
    }));
  };

  const handleFitToView = () => {
    if (elements.length === 0) return;
    
    const minX = Math.min(...elements.map(el => el.position_x));
    const maxX = Math.max(...elements.map(el => el.position_x + 160));
    const minY = Math.min(...elements.map(el => el.position_y));
    const maxY = Math.max(...elements.map(el => el.position_y + 65));
    
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const containerWidth = containerRef.current?.clientWidth || 1200;
    const containerHeight = containerRef.current?.clientHeight || 800;
    
    const scaleX = containerWidth / (contentWidth + 100);
    const scaleY = containerHeight / (contentHeight + 100);
    const scale = Math.min(scaleX, scaleY, 1);
    
    setViewTransform({
      x: (containerWidth - contentWidth * scale) / 2 - minX * scale,
      y: (containerHeight - contentHeight * scale) / 2 - minY * scale,
      scale
    });
  };

  // CRUD operations
  const createElementAtPosition = async (elementType, position) => {
    if (elementType !== ELEMENT_TYPES.OPPORTUNITY && actors.length === 0) {
      toast.warning(t('strategyMapping.createActorFirst'));
      return;
    }

    // Show modal for all element types when there are actors available (for actor selection or optional assignment)
    if ((elementType === ELEMENT_TYPES.CAPABILITY || elementType === ELEMENT_TYPES.INTENTION || elementType === ELEMENT_TYPES.OPPORTUNITY) && actors.length > 0) {
      setElementForm({
        id: null,
        element_type: elementType,
        name: `${t(`strategyMapping.elementTypes.${elementType}`)} ${elements.length + 1}`,
        description: '',
        actor_id: elementType === ELEMENT_TYPES.OPPORTUNITY ? null : (actors[0]?.id || null),
        position
      });
      setShowElementModal(true);
      return;
    }

    try {
      const elementData = {
        element_type: elementType,
        name: `${t(`strategyMapping.elementTypes.${elementType}`)} ${elements.length + 1}`,
        description: '',
        position_x: position.x,
        position_y: position.y,
        actor_id: elementType === ELEMENT_TYPES.OPPORTUNITY ? null : actors[0]?.id
      };
      
      await strategyMappingService.createElement(case_.id, elementData);
      fetchStrategyMapping();
    } catch (error) {
      toast.error(t('strategyMapping.createElementError'));
    }
  };

  const handleCreateActor = async () => {
    try {
      if (editingActor) {
        await strategyMappingService.updateActor(editingActor.id, actorForm);
      } else {
        await strategyMappingService.createActor(case_.id, actorForm);
      }
      setShowActorModal(false);
      setEditingActor(null);
      setActorForm({ name: '', description: '', primary_intention: '', color: COLOR_ACTOR_PER_DEFECTE });
      fetchStrategyMapping();
    } catch (error) {
      toast.error(editingActor ? t('strategyMapping.updateActorError') : t('strategyMapping.createActorError'));
    }
  };

  const handleCreateElement = async () => {
    try {
      const elementData = {
        element_type: elementForm.element_type,
        name: elementForm.name,
        description: elementForm.description,
        position_x: elementForm.position.x,
        position_y: elementForm.position.y,
        actor_id: elementForm.actor_id
      };
      
      await strategyMappingService.createElement(case_.id, elementData);
      setShowElementModal(false);
      setElementForm({
        element_type: '',
        name: '',
        description: '',
        actor_id: null,
        position: { x: 0, y: 0 }
      });
      fetchStrategyMapping();
    } catch (error) {
      toast.error(t('strategyMapping.createElementError'));
    }
  };

  const handleUpdateElement = async (elementId, updates) => {
    try {
      await strategyMappingService.updateElement(elementId, updates);
      
      // Update local state - make sure to preserve all existing properties
      setElements(prev => prev.map(el => 
        el.id === elementId ? { 
          ...el, 
          ...updates,
          // Ensure position coordinates are preserved if not in updates
          position_x: updates.position_x !== undefined ? updates.position_x : el.position_x,
          position_y: updates.position_y !== undefined ? updates.position_y : el.position_y,
          // Force re-render by updating a timestamp
          lastUpdated: Date.now()
        } : el
      ));
      
      // Send collaboration update
      
    } catch (error) {
      toast.error(t('strategyMapping.updateElementError'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedElements.length === 0) return;
    
    if (window.confirm(t('strategyMapping.confirmDeleteElements'))) {
      try {
        for (const elementId of selectedElements) {
          await strategyMappingService.deleteElement(elementId);
        }
        setSelectedElements([]);
        fetchStrategyMapping();
      } catch (error) {
        toast.error(t('strategyMapping.deleteElementError'));
      }
    }
  };

  const handleCreateConnection = async (sourceId, targetId) => {
    try {
      await strategyMappingService.createConnection(case_.id, {
        source_element_id: sourceId,
        target_element_id: targetId,
        strength: 'medium'
      });
      fetchStrategyMapping();
    } catch (error) {
      toast.error(t('strategyMapping.createConnectionError'));
    }
  };

  // Convertir elements del mapa en factors causals.
  //
  // Converteix TOTES les caixes seleccionades, una per element (se'n
  // selecciona més d'una amb Ctrl/Cmd + clic). Per això el botó porta el
  // recompte, com el d'esborrar: abans no ho deia i no sabies què faria.
  //
  // La categoria estava clavada a 'custom', o sigui que tot el que importaves
  // queia fora del PESTEL del cas. Ara es tria, amb la mateixa llista que fa
  // servir la passa 1.
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertCategory, setConvertCategory] = useState(null);

  const categoriesDelCas = useMemo(() => {
    const sistema = case_?.category_system || 'PESTEL';
    if (sistema === 'CUSTOM' && case_?.custom_categories?.length) {
      return case_.custom_categories.map(c => ({ value: c, label: c }));
    }
    const perSistema = {
      PESTEL: ['political', 'economic', 'social', 'technological', 'environmental', 'legal'],
      SWOT: ['strengths', 'weaknesses', 'opportunities', 'threats'],
      CYBER: ['identify', 'protect', 'detect', 'respond', 'recover']
    };
    const claus = perSistema[sistema] || perSistema.PESTEL;
    const prefix = { PESTEL: 'pestel', SWOT: 'swot', CYBER: 'cyber' }[sistema] || 'pestel';
    return claus.map(c => ({ value: c, label: t(`factors.${prefix}.${c}`) }));
  }, [case_, t]);

  const obreConversio = () => {
    if (selectedElements.length === 0) {
      toast.warning(t('strategyMapping.selectElementsFirst'));
      return;
    }
    setConvertCategory(categoriesDelCas[0]?.value || 'custom');
    setShowConvertModal(true);
  };

  const handleConvertToFactors = async () => {
    try {
      const result = await strategyMappingService.convertToFactors(case_.id, {
        element_ids: selectedElements,
        category: convertCategory || 'custom'
      });
      setShowConvertModal(false);
      // Aquesta confirmació es queda: els factors apareixen en una ALTRA
      // pestanya, o sigui que aquí no es veu que hagi passat res. I el servidor
      // salta els elements que ja tenen un factor amb el mateix nom, així que
      // el nombre creat pot ser menor que el que has seleccionat.
      toast.success(result.message);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      toast.error(t('strategyMapping.convertError'));
    }
  };

  // Exportació del llenç
  //
  // Les dues exportacions —PNG i PDF— comparteixen la feina de convertir l'SVG
  // en una imatge, i totes dues estaven trencades de la mateixa manera:
  // calculaven els límits del dibuix i després no els feien servir, perquè
  // `drawImage(img, 0, 0)` dibuixa a l'origen. Si els elements estaven lluny de
  // l'origen, el resultat sortia descentrat i retallat. A més, l'SVG que se
  // serialitzava tenia la mida de la finestra, no la del contingut.
  //
  // Ara es clona l'SVG, se li posa un viewBox que envolta exactament el
  // contingut, i el llenç es fa d'aquesta mida.
  const construeixLlenc = () => new Promise((resolve, reject) => {
    const svgElement = canvasRef.current;
    if (!svgElement) { reject(new Error('no hi ha llenç')); return; }

    const marge = 60;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    elements.forEach(el => {
      minX = Math.min(minX, el.position_x);
      minY = Math.min(minY, el.position_y);
      maxX = Math.max(maxX, el.position_x + AMPLE_CAIXA);
      // L'alçada real de cada caixa, no un 65 escrit a mà: des que el títol
      // s'ajusta, les caixes no fan totes el mateix.
      maxY = Math.max(maxY, el.position_y + alturaCaixa(el, actors));
    });
    if (!elements.length) { minX = 0; minY = 0; maxX = 800; maxY = 600; }

    const x = minX - marge, y = minY - marge;
    const width = (maxX - minX) + marge * 2;
    const height = (maxY - minY) + marge * 2;

    const clon = svgElement.cloneNode(true);
    clon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clon.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
    clon.setAttribute('width', width);
    clon.setAttribute('height', height);
    // El zoom i el desplaçament de la pantalla no han de sortir a l'exportació
    const grup = clon.querySelector('[data-transformable]');
    if (grup) grup.removeAttribute('transform');

    const svgData = new XMLSerializer().serializeToString(clon);
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      // ×2 perquè el text no surti pixelat
      const escala = 2;
      canvas.width = width * escala;
      canvas.height = height * escala;
      const ctx = canvas.getContext('2d');
      ctx.scale(escala, escala);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve({ canvas, width, height });
    };
    // Sense això, un SVG que no carrega deixava l'exportació penjada en silenci
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('no s_ha pogut convertir l_SVG')); };
    img.src = url;
  });

  const handleExportImage = async () => {
    try {
      const { canvas } = await construeixLlenc();
      const link = document.createElement('a');
      link.download = `mapa-estrategic-${case_.name || 'diagrama'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      // La confirmació anava ABANS del onload: deia «exportat» encara que
      // fallés. Ara va aquí, quan el fitxer ja s'ha demanat.
      toast.success(t('strategyMapping.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('strategyMapping.exportError'));
    }
  };

  const handleExportPDF = async () => {
    try {
      // jsPDF es carrega només quan cal, des del nostre propi servidor.
      // Abans venia d'un CDN i es mirava `window.jsPDF`, que no existeix mai:
      // la versió UMD exposa `window.jspdf`, un objecte que conté jsPDF a dins.
      // O sigui que aquest botó sempre es queixava que faltava la llibreria.
      // De passada, l'aplicació ja no depèn de cap servei extern.
      const { jsPDF } = await import('jspdf');
      const { canvas, width, height } = await construeixLlenc();

      const pdf = new jsPDF({
        orientation: width > height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [width, height]
      });
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, width, height);
      pdf.save(`mapa-estrategic-${case_.name || 'diagrama'}.pdf`);
      toast.success(t('strategyMapping.exportSuccess'));
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(t('strategyMapping.exportError'));
    }
  };

  const getConnectionPoints = (sourceEl, targetEl) => {
    const sourceH = alturaCaixa(sourceEl, actors);
    const targetH = alturaCaixa(targetEl, actors);
    const sourceCenter = { x: sourceEl.position_x + AMPLE_CAIXA / 2, y: sourceEl.position_y + sourceH / 2 };
    const targetCenter = { x: targetEl.position_x + AMPLE_CAIXA / 2, y: targetEl.position_y + targetH / 2 };
    
    // Calculate which sides to connect based on relative positions
    const dx = targetCenter.x - sourceCenter.x;
    const dy = targetCenter.y - sourceCenter.y;
    
    let sourcePoint, targetPoint;
    
    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal connection preferred
      if (dx > 0) {
        // Source right to target left
        sourcePoint = { x: sourceEl.position_x + AMPLE_CAIXA, y: sourceCenter.y };
        targetPoint = { x: targetEl.position_x, y: targetCenter.y };
      } else {
        // Source left to target right
        sourcePoint = { x: sourceEl.position_x, y: sourceCenter.y };
        targetPoint = { x: targetEl.position_x + AMPLE_CAIXA, y: targetCenter.y };
      }
    } else {
      // Vertical connection preferred
      if (dy > 0) {
        // Source bottom to target top
        sourcePoint = { x: sourceCenter.x, y: sourceEl.position_y + sourceH };
        targetPoint = { x: targetCenter.x, y: targetEl.position_y };
      } else {
        // Source top to target bottom
        sourcePoint = { x: sourceCenter.x, y: sourceEl.position_y };
        targetPoint = { x: targetCenter.x, y: targetEl.position_y + targetH };
      }
    }
    
    return { sourcePoint, targetPoint };
  };

  const renderConnection = (connection) => {
    const source = elements.find(el => el.id === connection.source_element_id);
    const target = elements.find(el => el.id === connection.target_element_id);
    
    if (!source || !target) return null;

    const { sourcePoint, targetPoint } = getConnectionPoints(source, target);
    const midX = (sourcePoint.x + targetPoint.x) / 2;
    const midY = (sourcePoint.y + targetPoint.y) / 2;
    
    // Create a curved path for more elegant connections
    const curvature = 0.3;
    const dx = targetPoint.x - sourcePoint.x;
    const dy = targetPoint.y - sourcePoint.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const offset = distance * curvature;
    
    let controlX, controlY;
    if (Math.abs(dx) > Math.abs(dy)) {
      controlX = midX;
      controlY = sourcePoint.y + (dy > 0 ? offset : -offset);
    } else {
      controlX = sourcePoint.x + (dx > 0 ? offset : -offset);
      controlY = midY;
    }

    const pathData = `M ${sourcePoint.x} ${sourcePoint.y} Q ${controlX} ${controlY} ${targetPoint.x} ${targetPoint.y}`;

    return (
      <g key={connection.id}>
        <path
          d={pathData}
          stroke="#9CA3AF"
          strokeWidth="1.5"
          fill="none"
          markerEnd="url(#arrowhead)"
          opacity="0.7"
        />
      </g>
    );
  };

  const renderElement = (element) => {
    const isSelected = selectedElements.includes(element.id);
    const isConnectionStart = connectionStart?.id === element.id;
    
    // More robust actor lookup with fallback
    const actor = element.actor_id ? actors.find(a => a.id === element.actor_id) : null;
    
    let color;
    if (element.element_type === ELEMENT_TYPES.ACTOR) {
      color = actor?.color || ELEMENT_COLORS[ELEMENT_TYPES.ACTOR];
    } else if (element.element_type === ELEMENT_TYPES.OPPORTUNITY) {
      color = ELEMENT_COLORS[ELEMENT_TYPES.OPPORTUNITY];
    } else {
      // Capabilities and intentions use their actor's color
      color = actor?.color || ELEMENT_COLORS[ELEMENT_TYPES.ACTOR];
    }

    // Show actor name for capabilities, intentions, and opportunities (for opportunities if they have an actor)
    const showActorInfo = (element.element_type === ELEMENT_TYPES.CAPABILITY || 
                          element.element_type === ELEMENT_TYPES.INTENTION ||
                          (element.element_type === ELEMENT_TYPES.OPPORTUNITY && actor)) && actor;

    const caixa = distribucioCaixa(element, showActorInfo);
    // Si un company té aquesta caixa agafada, se li pinta un anell del seu
    // color amb el seu nom a sobre: així veus qui treballa on sense preguntar.
    const altri = propietariRemot(element.id);

    return (
      <g
        key={element.id}
        transform={`translate(${element.position_x}, ${element.position_y})`}
        onMouseDown={(e) => handleElementMouseDown(element, e)}
        onClick={(e) => handleElementClick(element, e)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (element.element_type === ELEMENT_TYPES.OPPORTUNITY || 
              element.element_type === ELEMENT_TYPES.CAPABILITY || 
              element.element_type === ELEMENT_TYPES.INTENTION) {
            setElementForm({
              id: element.id, // Include element ID for editing
              element_type: element.element_type,
              name: element.name,
              description: element.description || '',
              actor_id: element.actor_id,
              position: { x: element.position_x, y: element.position_y }
            });
            setShowElementModal(true);
          }
        }}
        style={{ cursor: activeTool === TOOLS.SELECT ? 'grab' : 'pointer' }}
      >
        {altri && (
          <>
            <rect
              x="-4" y="-4"
              width={AMPLE_CAIXA + 8}
              height={caixa.alt + 8}
              rx="9"
              fill="none"
              stroke={altri.color}
              strokeWidth="2.5"
              pointerEvents="none"
            />
            <rect
              x="-4" y={-21}
              width={Math.min(AMPLE_CAIXA + 8, 9 + (altri.nom || '').length * 6)}
              height="16" rx="4"
              fill={altri.color}
              pointerEvents="none"
            />
            <text
              x="1" y={-9}
              fill="white" fontSize="10" fontWeight="600"
              pointerEvents="none"
            >
              {altri.nom}
            </text>
          </>
        )}
        <rect
          width={AMPLE_CAIXA}
          height={caixa.alt}
          rx="6"
          fill={color}
          /* Opacitat plena: abans era 0,7 i el color que es veia era la barreja
             amb el fons blanc, o sigui que dos colors que se separaven bé
             s'acostaven en pantalla. La selecció ja es distingeix pel contorn. */
          stroke={isSelected ? '#111827' : isConnectionStart ? '#EF4444' : color}
          strokeWidth={isSelected || isConnectionStart ? 3 : 1}
          strokeDasharray={isConnectionStart ? '4,4' : 'none'}
        />
        {/* El títol es reparteix en línies que caben a la caixa. Un <text> de
            SVG no ajusta ni retalla: si el nom era més llarg que la caixa,
            sortia per tots dos costats. */}
        {caixa.linies.map((linia, i) => (
          <text
            key={i}
            x={AMPLE_CAIXA / 2}
            y={caixa.yTitol[i]}
            textAnchor="middle"
            fill="white"
            fontSize="13"
            fontWeight="600"
            pointerEvents="none"
          >
            {linia}
          </text>
        ))}
        <title>{element.name}</title>
        <text
          x={AMPLE_CAIXA / 2}
          y={caixa.yTipus}
          textAnchor="middle"
          fill="white"
          fontSize="10"
          opacity="0.9"
          pointerEvents="none"
        >
          {t(`strategyMapping.elementTypes.${element.element_type}`)}
        </text>
        {showActorInfo && (
          /* El nom de l'actor també se sortia de la caixa quan era llarg */
          <text
            x={AMPLE_CAIXA / 2}
            y={caixa.yActor}
            textAnchor="middle"
            fill="white"
            fontSize="10"
            opacity="0.85"
            fontStyle="italic"
            pointerEvents="none"
          >
            {partirTitol(actor.name, 10)[0]}
          </text>
        )}
      </g>
    );
  };

  return (
    <div ref={fullscreenRef} className="flex h-full relative min-h-screen">
      {/* Floating Actors Panel */}
      {!isSidebarCollapsed && (
        <div className={`absolute top-16 left-4 w-72 max-h-96 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden ${isFullscreen ? 'z-[9998]' : 'z-40'}`}>
          <div className="p-3 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-neutral-900">{t('strategyMapping.actors')}</h3>
                <p className="text-xs text-neutral-500 mt-0.5">{actors.length} {actors.length === 1 ? 'actor' : 'actors'}</p>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setShowActorModal(true)}
                  className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors flex items-center space-x-1 text-xs font-medium"
                  title={t('strategyMapping.tools.addActor')}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('strategyMapping.addActor')}</span>
                </button>
                <button
                  onClick={() => setIsSidebarCollapsed(true)}
                  className="p-1 hover:bg-neutral-100 rounded text-neutral-400 hover:text-neutral-600 transition-colors"
                  title="Close panel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        
          <div className="max-h-64 overflow-y-auto p-3">
            {actors.length === 0 ? (
              <div className="text-center py-3">
                <svg className="w-6 h-6 mx-auto text-neutral-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-neutral-500 text-xs">{t('strategyMapping.noActors')}</p>
                <p className="text-neutral-400 text-xs mt-1">{t('strategyMapping.createActorHere')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {actors.map(actor => {
                  const actorElements = elements.filter(el => el.actor_id === actor.id);
                  const capabilities = actorElements.filter(el => el.element_type === ELEMENT_TYPES.CAPABILITY);
                  const intentions = actorElements.filter(el => el.element_type === ELEMENT_TYPES.INTENTION);
                  
                  return (
                    <div
                      key={actor.id}
                      className="border border-neutral-200 rounded p-2 hover:border-neutral-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: actor.color }}
                            ></div>
                            <h4 className="font-medium text-neutral-900 text-xs truncate">{actor.name}</h4>
                          </div>
                          <p className="text-xs text-neutral-500 mt-1 line-clamp-2">
                            "{actor.primary_intention}"
                          </p>
                          
                          <div className="mt-1 flex space-x-2 text-xs text-neutral-500">
                            <span>{capabilities.length} cap.</span>
                            <span>{intentions.length} int.</span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => {
                            setEditingActor(actor);
                            setActorForm({
                              name: actor.name,
                              description: actor.description || '',
                              primary_intention: actor.primary_intention,
                              color: actor.color
                            });
                            setShowActorModal(true);
                          }}
                          className="text-neutral-400 hover:text-neutral-600 p-0.5 ml-1 flex-shrink-0"
                          title="Edit actor"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      
      
      {/* Main Canvas Area */}
      <div ref={containerRef} className="w-full flex flex-col h-full" style={{
        backgroundColor: isFullscreen ? '#f9fafb' : 'transparent',
        zIndex: isFullscreen ? 1 : 'auto'
      }}>
        {/* Toolbox */}
        <div className="bg-white border-b border-neutral-200 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {/* Selection Tools */}
              <div className="flex bg-neutral-100 rounded-lg p-1">
                <button
                  onClick={() => handleToolChange(TOOLS.SELECT)}
                  className={`p-2 rounded ${activeTool === TOOLS.SELECT ? 'bg-white shadow-sm' : 'hover:bg-neutral-200'}`}
                  title={t('strategyMapping.tools.select')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleToolChange(TOOLS.PAN)}
                  className={`p-2 rounded ${activeTool === TOOLS.PAN ? 'bg-white shadow-sm' : 'hover:bg-neutral-200'}`}
                  title={t('strategyMapping.tools.pan')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v4.5a.5.5 0 001 0V6a1 1 0 112 0v6a7 7 0 11-14 0V9a1 1 0 012 0v2.5a.5.5 0 001 0V4a1 1 0 012 0v4.5a.5.5 0 001 0V3z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="w-px h-6 bg-neutral-300" />

              {/* Element Tools */}
              <div className="flex space-x-1">
                <button
                  onClick={() => handleToolChange(TOOLS.ADD_CAPABILITY)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-1 transition-colors ${
                    activeTool === TOOLS.ADD_CAPABILITY 
                      ? 'bg-neutral-200 text-neutral-900 border border-neutral-300' 
                      : 'bg-white hover:bg-neutral-50 text-neutral-700 border border-neutral-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('strategyMapping.tools.addCapability')}</span>
                </button>
                <button
                  onClick={() => handleToolChange(TOOLS.ADD_OPPORTUNITY)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-1 transition-colors ${
                    activeTool === TOOLS.ADD_OPPORTUNITY 
                      ? 'bg-neutral-200 text-neutral-900 border border-neutral-300' 
                      : 'bg-white hover:bg-neutral-50 text-neutral-700 border border-neutral-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('strategyMapping.tools.addOpportunity')}</span>
                </button>
                <button
                  onClick={() => handleToolChange(TOOLS.ADD_INTENTION)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-1 transition-colors ${
                    activeTool === TOOLS.ADD_INTENTION 
                      ? 'bg-neutral-200 text-neutral-900 border border-neutral-300' 
                      : 'bg-white hover:bg-neutral-50 text-neutral-700 border border-neutral-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('strategyMapping.tools.addIntention')}</span>
                </button>
              </div>

              <div className="w-px h-6 bg-neutral-300" />

              {/* Connection Tool */}
              <button
                onClick={() => handleToolChange(TOOLS.CONNECT)}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors ${
                  activeTool === TOOLS.CONNECT 
                    ? 'bg-neutral-200 text-neutral-900 border border-neutral-300' 
                    : 'bg-white hover:bg-neutral-50 text-neutral-700 border border-neutral-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </button>

              <div className="w-px h-6 bg-neutral-300" />

              {/* View Tools */}
              <div className="flex space-x-1">
                <div className="flex bg-neutral-100 rounded-lg p-1">
                  <button
                    onClick={() => handleZoom('in')}
                    className="p-2 rounded hover:bg-neutral-200"
                    title={t('strategyMapping.tools.zoomIn')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleZoom('out')}
                    className="p-2 rounded hover:bg-neutral-200"
                    title={t('strategyMapping.tools.zoomOut')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleFitToView}
                    className="p-2 rounded hover:bg-neutral-200"
                    title={t('strategyMapping.tools.fitToView')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewTransform({ x: 0, y: 0, scale: 1 })}
                    className="p-2 rounded hover:bg-neutral-200"
                    title="Reset View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button
                    onClick={handleToggleFullscreen}
                    className={`p-2 rounded transition-colors ${
                      isFullscreen ? 'bg-blue-100 text-blue-600' : 'hover:bg-neutral-200'
                    }`}
                    title={isFullscreen ? t('strategyMapping.exitFullscreen') : t('strategyMapping.fullscreen')}
                  >
                    {isFullscreen ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {/* Quatre angles, que és la icona d'ampliar de tota la vida.
                            Abans hi havia també un quadrat al mig i semblava un botó
                            d'aturar o de retallar. */}
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9V5a1 1 0 011-1h4M15 4h4a1 1 0 011 1v4M20 15v4a1 1 0 01-1 1h-4M9 20H5a1 1 0 01-1-1v-4" />
                      </svg>
                    )}
                  </button>
                  {/* Delete button - always visible */}
                  <button
                    onClick={handleDeleteSelected}
                    className={`p-2 rounded transition-colors ${
                      selectedElements.length > 0 
                        ? 'bg-red-100 hover:bg-red-200 text-red-700' 
                        : 'text-neutral-400 cursor-not-allowed'
                    }`}
                    title={selectedElements.length > 0 ? `${t('common.delete')} (${selectedElements.length})` : t('common.delete')}
                    disabled={selectedElements.length === 0}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              {/* Export Dropdown */}
              <div className="relative export-menu">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                  title={t('strategyMapping.export')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
                {showExportMenu && (
                  <div className={`absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 ${isFullscreen ? 'z-[9997]' : 'z-50'}`}>
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleExportImage();
                          setShowExportMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{t('strategyMapping.exportImage')}</span>
                      </button>
                      <button
                        onClick={() => {
                          handleExportPDF();
                          setShowExportMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>{t('strategyMapping.exportPDF')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>


              {/* Help Button */}
              <button
                onClick={() => setShowHelp(true)}
                className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                title={t('strategyMapping.help')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              {/* Convert to factors button */}
              {selectedElements.length > 0 && (
                <button
                  onClick={obreConversio}
                  className="px-3 py-2 bg-neutral-100 text-neutral-700 border border-neutral-200 rounded-lg text-sm hover:bg-neutral-200 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <span>{t('strategyMapping.convertToFactors')} ({selectedElements.length})</span>
                </button>
              )}
              
              <span className="text-sm text-neutral-600 max-w-xs">
                {activeTool === TOOLS.CONNECT && !connectionStart && t('strategyMapping.selectFirstElement')}
                {activeTool === TOOLS.CONNECT && connectionStart && t('strategyMapping.selectSecondElement')}
                {activeTool.startsWith('add_') && activeTool !== TOOLS.ADD_ACTOR && t('strategyMapping.clickToPlace')}
                {activeTool === TOOLS.ADD_ACTOR && t('strategyMapping.clickToCreateActor')}
              </span>
            </div>
          </div>
        </div>
        
        {/* Canvas */}
        <div className="flex-1 bg-neutral-50 overflow-hidden relative min-h-[600px]">
          <svg
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="w-full h-full"
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onClick={handleCanvasClick}
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="8"
                markerHeight="8"
                refX="7"
                refY="3"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <polygon
                  points="0 0, 8 3, 0 6"
                  fill="#9CA3AF"
                />
              </marker>
              
              {/* Grid pattern */}
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#d1d5db" strokeWidth="1"/>
                <path d="M 10 0 L 10 20 M 0 10 L 20 10" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
              </pattern>
            </defs>
            
            {/* Main content group with transform */}
            {/* data-transformable: l'exportació el busca per treure-li el zoom
                i el desplaçament, perquè el PDF surti del contingut i no del
                que es veu a la finestra en aquell moment. */}
            <g data-transformable transform={`translate(${viewTransform.x}, ${viewTransform.y}) scale(${viewTransform.scale})`}>
              {/* Infinite grid */}
              <rect 
                x="-10000" 
                y="-10000" 
                width="20000" 
                height="20000" 
                fill="url(#grid)" 
              />
            
              {/* Render connections */}
              {connections.map(renderConnection)}
              
              {/* Render elements */}
              {elements.map(element => renderElement(element))}
            </g>
          </svg>

          {/* Canvas Info */}
          <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-sm border border-neutral-200 p-3">
            <div className="text-xs text-neutral-600 space-y-1">
              <div className="flex items-center justify-between space-x-4">
                <span>{t('strategyMapping.elements')}:</span>
                <span className="font-medium">{elements.length}</span>
              </div>
              <div className="flex items-center justify-between space-x-4">
                <span>{t('strategyMapping.connections')}:</span>
                <span className="font-medium">{connections.length}</span>
              </div>
              <div className="flex items-center justify-between space-x-4">
                <span>{t('strategyMapping.zoom')}:</span>
                <span className="font-medium">{Math.round(viewTransform.scale * 100)}%</span>
              </div>
            </div>
          </div>
          
          {/* Actors Toggle Button - Inside Canvas */}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={`absolute top-4 left-4 px-3 py-2 bg-white border border-neutral-200 rounded-lg shadow-sm hover:bg-neutral-50 transition-colors flex items-center space-x-2 ${isFullscreen ? 'z-[9996]' : 'z-30'}`}
            title={isSidebarCollapsed ? t('strategyMapping.showActors') : t('strategyMapping.hideActors')}
          >
            <svg className="w-4 h-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-sm text-neutral-700 font-medium">{t('strategyMapping.actors')}</span>
          </button>
        </div>
      </div>
      
      {/* Actor Modal */}
      {showActorModal && renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" style={{ position: 'fixed' }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-neutral-900 mb-4">
              {editingActor ? t('strategyMapping.editActor') : t('strategyMapping.createActor')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.name')}
                </label>
                <input
                  type="text"
                  value={actorForm.name}
                  onChange={(e) => setActorForm({ ...actorForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('strategyMapping.primaryIntention')}
                </label>
                <input
                  type="text"
                  value={actorForm.primary_intention}
                  onChange={(e) => setActorForm({ ...actorForm, primary_intention: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.description')}
                </label>
                <textarea
                  value={actorForm.description}
                  onChange={(e) => setActorForm({ ...actorForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('strategyMapping.color')}
                </label>
                <div className="space-y-3">
                  {/* Predefined Color Palette */}
                  <div className="grid grid-cols-6 gap-2">
                    {COLORS_ACTOR.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setActorForm({ ...actorForm, color })}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          actorForm.color === color 
                            ? 'border-neutral-400 scale-110 shadow-lg' 
                            : 'border-neutral-200 hover:border-neutral-300 hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  {/* Custom Color Picker */}
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">
                      {t('strategyMapping.customColor')}
                    </label>
                    <input
                      type="color"
                      value={actorForm.color}
                      onChange={(e) => setActorForm({ ...actorForm, color: e.target.value })}
                      className="w-full h-10 rounded-lg border border-neutral-300"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowActorModal(false);
                  setEditingActor(null);
                  setActorForm({ name: '', description: '', primary_intention: '', color: COLOR_ACTOR_PER_DEFECTE });
                  setActiveTool(TOOLS.SELECT);
                }}
                className="px-4 py-2 text-neutral-700 hover:text-neutral-900"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateActor}
                disabled={!actorForm.name || !actorForm.primary_intention}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingActor ? t('common.update') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Diàleg de conversió a factors causals */}
      {showConvertModal && renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              {t('strategyMapping.convertToFactors')}
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              {t('strategyMapping.convertExplain', { count: selectedElements.length })}
            </p>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('factors.category')}
            </label>
            <select
              value={convertCategory || ''}
              onChange={(e) => setConvertCategory(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              {categoriesDelCas.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mb-5">{t('strategyMapping.convertSkipNote')}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConvertModal(false)}
                className="px-4 py-2 text-sm text-neutral-700 border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConvertToFactors}
                className="px-4 py-2 text-sm font-semibold text-white bg-accent-700 rounded-lg hover:bg-accent-800"
              >
                {t('strategyMapping.convertToFactors')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Element Modal */}
      {showElementModal && renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-neutral-900 mb-4">
              {elementForm.name ? 
                `${t('common.edit')} ${t(`strategyMapping.elementTypes.${elementForm.element_type}`)}` :
                t('strategyMapping.createElement')
              }
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.name')}
                </label>
                <input
                  type="text"
                  value={elementForm.name}
                  onChange={(e) => setElementForm({ ...elementForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
              {/* Only show actor selection for capabilities and intentions, not opportunities */}
              {elementForm.element_type !== ELEMENT_TYPES.OPPORTUNITY && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {t('strategyMapping.selectActor')}
                  </label>
                  <select
                    value={elementForm.actor_id || ''}
                    onChange={(e) => setElementForm({ ...elementForm, actor_id: e.target.value || null })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                  >
                    <option value="">{t('strategyMapping.selectAnActor')}</option>
                    {actors.map(actor => (
                      <option key={actor.id} value={actor.id}>
                        {actor.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('common.description')}
                </label>
                <textarea
                  value={elementForm.description}
                  onChange={(e) => setElementForm({ ...elementForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                  placeholder={t('strategyMapping.elementDescriptionPlaceholder')}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowElementModal(false);
                  setElementForm({
                    id: null,
                    element_type: '',
                    name: '',
                    description: '',
                    actor_id: null,
                    position: { x: 0, y: 0 }
                  });
                  setActiveTool(TOOLS.SELECT);
                }}
                className="px-4 py-2 text-neutral-700 hover:text-neutral-900"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={elementForm.id ? 
                  async () => {
                    await handleUpdateElement(elementForm.id, {
                      name: elementForm.name,
                      description: elementForm.description,
                      actor_id: elementForm.actor_id
                    });
                    setShowElementModal(false);
                    setElementForm({
                      id: null,
                      element_type: '',
                      name: '',
                      description: '',
                      actor_id: null,
                      position: { x: 0, y: 0 }
                    });
                  } : 
                  handleCreateElement
                }
                disabled={!elementForm.name || (elementForm.element_type !== ELEMENT_TYPES.OPPORTUNITY && !elementForm.actor_id)}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {elementForm.id ? t('common.update') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Help Modal */}
      {showHelp && renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-neutral-900">
                {t('strategyMapping.helpTitle')}
              </h3>
              <button
                onClick={() => setShowHelp(false)}
                className="text-neutral-400 hover:text-neutral-600 p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Overview */}
              <div>
                <p className="text-neutral-600 text-sm leading-relaxed">
                  {t('strategyMapping.helpOverview')}
                </p>
              </div>

              {/* Workflow */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 mb-3">{t('strategyMapping.helpWorkflow')}</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-neutral-600">
                  <li>{t('strategyMapping.helpWorkflowStep1')}</li>
                  <li>{t('strategyMapping.helpWorkflowStep2')}</li>
                  <li>{t('strategyMapping.helpWorkflowStep3')}</li>
                  <li>{t('strategyMapping.helpWorkflowStep4')}</li>
                  <li>{t('strategyMapping.helpWorkflowStep5')}</li>
                </ol>
              </div>
              
              {/* Interface Guide */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 mb-3">{t('strategyMapping.helpInterface')}</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-neutral-600">
                  <li>{t('strategyMapping.helpToolbox')}</li>
                  <li>{t('strategyMapping.helpActorsPanel')}</li>
                  <li>{t('strategyMapping.helpCanvas')}</li>
                  <li>{t('strategyMapping.helpExport')}</li>
                </ul>
              </div>

              {/* Element Types Quick Reference */}
              <div>
                <h4 className="text-lg font-medium text-neutral-900 mb-3">{t('strategyMapping.helpTools')}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-4 h-4 rounded bg-blue-500"></div>
                    <span className="font-medium">{t('strategyMapping.elementTypes.actor')}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-4 h-4 rounded bg-neutral-400"></div>
                    <span className="font-medium">{t('strategyMapping.elementTypes.capability')}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-4 h-4 rounded bg-slate-500"></div>
                    <span className="font-medium">{t('strategyMapping.elementTypes.opportunity')}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-4 h-4 rounded bg-neutral-400"></div>
                    <span className="font-medium">{t('strategyMapping.elementTypes.intention')}</span>
                  </div>
                </div>
              </div>

              {/* Collaboration Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-sm text-blue-800">{t('strategyMapping.helpCollaboration')}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowHelp(false)}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Exportats per poder-los provar sols: són la part més fàcil d'equivocar.
export { partirTitol, distribucioCaixa, AMPLE_CAIXA };

export default StrategyMapping;